import { createContext, ReactNode, useContext } from 'react';
import { apiV2 } from '../services/api';

type EntityId = string | number;
type RemunerationCategory = 'PRODUCTION' | 'COMMISSION';
type RemunerationBalanceDirection = 'EMPLOYEE_CREDIT' | 'EMPLOYEE_DEBIT';

export interface RemunerationBalanceOrigin {
  id_remuneration_balance_origin: EntityId;
  id_round: EntityId;
  harvest: number | null;
  round_number: number | null;
  id_trip: EntityId;
  boat_name: string;
  trip_number: number | null;
  original_amount: number;
  remaining_amount: number;
  settlements: Array<{
    id_settlement: EntityId;
    amount: number;
    id_target_trip: EntityId | null;
    target_boat_name: string | null;
    source_order: {
      id_order: EntityId;
      total_amount: number;
      due_dates: string[];
      payment_dates: string[];
      status: string[];
      is_active: boolean;
      payment_type: {
        id_paymenttype: EntityId;
        paymenttype_name: string;
      };
    } | null;
    target_order: {
      id_order: EntityId;
      total_amount: number;
      status: string[];
      is_active: boolean;
    } | null;
    created_at: string;
    actions?: { can_cancel: boolean; can_pay: boolean; can_reverse_payment: boolean };
  }>;
}

export interface CancellableRemunerationBalancePayment {
  id_settlement: EntityId;
  amount: number;
  category: RemunerationCategory;
  employee: { id_employee: EntityId; full_name: string };
  origin: {
    id_remuneration_balance_origin: EntityId;
    id_round: EntityId;
    harvest: number | null;
    round_number: number | null;
    id_trip: EntityId;
    boat_name: string;
    trip_number: number | null;
  };
  source_order: {
    id_order: EntityId;
    total_amount: number;
    due_dates: string[];
    payment_dates: string[];
    status: string[];
    is_active: boolean;
    payment_type: {
      id_paymenttype: EntityId;
      paymenttype_name: string;
    };
  } | null;
  created_at: string;
  actions: { can_cancel: boolean; can_pay: boolean; can_reverse_payment: boolean };
}

export interface RemunerationBalance {
  id_remuneration_balance: EntityId;
  employee: { id_employee: EntityId; full_name: string };
  category: RemunerationCategory;
  remaining_amount: number;
  closing: {
    id_round: EntityId;
    reference_date: string;
    closed_at: string | null;
  };
  origins: RemunerationBalanceOrigin[];
}

type Response<T = undefined> = { success: boolean; message: string; data?: T };
type Filter = { employee_id?: EntityId; category?: RemunerationCategory; trip_id?: EntityId; direction?: RemunerationBalanceDirection; only_available?: boolean };
type CancellablePaymentsFilter = { page?: number; pageSize?: number; employee_id?: EntityId; category?: RemunerationCategory };

interface ContextData {
  getRemunerationBalances: (filter?: Filter) => Promise<Response<RemunerationBalance[]>>;
  getRemunerationBalance: (balanceId: EntityId) => Promise<Response<RemunerationBalance>>;
  getCancellableRemunerationBalancePayments: (filter?: CancellablePaymentsFilter) => Promise<Response<CancellableRemunerationBalancePayment[]>>;
  createRemunerationBalancePayment: (allocations: Array<{ origin_id: EntityId; amount: number }>) => Promise<Response>;
  cancelRemunerationBalancePayment: (settlementId: EntityId) => Promise<Response>;
  payRemunerationBalanceSettlement: (settlementId: EntityId, data: { payment_date: string; payment_type_id: EntityId }) => Promise<Response>;
  reverseRemunerationBalanceSettlementPayment: (settlementId: EntityId) => Promise<Response>;
}

const RemunerationBalancesContext = createContext<ContextData>({} as ContextData);

function apiMessage(response: any) {
  return response?.data?.message || response?.message;
}

function translate(message: unknown) {
  if (typeof message !== 'string') return undefined;
  const value = message.trim();
  if (/^remuneration balance not found\.?$/i.test(value)) return 'Saldo de remuneração não encontrado.';
  if (/^target trip with a begin date was not found\.?$/i.test(value)) return 'A viagem precisa ter uma data de início para consultar os saldos disponíveis.';
  if (/^you do not have permission to perform this action\.?$/i.test(value)) return 'Você não tem permissão para realizar esta ação.';
  if (/^unauthorized\.?$/i.test(value)) return 'Sua sessão expirou. Faça login novamente.';
  if (/^validation failed\.?$/i.test(value)) return 'Não foi possível validar os saldos informados.';
  if (/^a positive remuneration balance payment cannot exceed its available amount\.?$/i.test(value)) return 'O valor informado é maior que o saldo disponível para pagamento.';
  if (/^the source trip must have a begin date before paying a remuneration balance\.?$/i.test(value)) return 'A viagem de origem precisa ter uma data de início antes de gerar o pedido.';
  if (/^only production and commission remuneration balances can be paid\.?$/i.test(value)) return 'Somente saldos de produção ou comissão podem gerar pedidos.';
  if (/^standalone remuneration balance payment not found\.?$/i.test(value)) return 'Pedido de diferença de produção não encontrado.';
  if (/^standalone positive remuneration balance payment not found\.?$/i.test(value)) return 'Pedido de diferença de produção não encontrado.';
  if (/^a remuneration balance payment can only be cancelled before it is executed\.?$/i.test(value)) return 'Apenas pedidos pendentes de diferença de produção podem ser removidos.';
  if (/^payment type is inactive or does not belong to this company\.?$/i.test(value)) return 'O tipo de pagamento selecionado está inativo ou não pertence à empresa.';
  if (/^only an active pending remuneration balance payment can be paid\.?$/i.test(value)) return 'Apenas pedidos pendentes e ativos de diferença de produção podem ser pagos.';
  if (/^only an active executed remuneration balance payment can be reversed\.?$/i.test(value)) return 'Apenas pagamentos executados e ativos de diferença de produção podem ser desfeitos.';
  if (/^active payment type dinheiro was not found for this company\.?$/i.test(value)) return 'O tipo de pagamento Dinheiro precisa estar ativo para gerar o pedido.';
  if (/^active cost center named .+ was not found for the source trip\.?$/i.test(value)) return 'O centro de custo do barco de origem precisa estar ativo para gerar o pedido.';
  if (/^active cost category produ[çc][aã]o was not found for this company\.?$/i.test(value)) return 'A categoria de custo PRODUÇÃO precisa estar ativa para gerar o pedido.';
  if (/^user not found\.?$/i.test(value)) return 'Usuário não encontrado para gerar o pedido.';
  if (/^a remuneration balance origin can only be paid once per request\.?$/i.test(value)) return 'O mesmo saldo não pode ser informado mais de uma vez no pedido.';
  if (/^one or more remuneration balance origins were not found for this company\.?$/i.test(value)) return 'Um ou mais saldos de produção não foram encontrados.';
  return undefined;
}

function isTechnicalMessage(message: unknown) {
  if (typeof message !== 'string') return true;
  return /^(cannot\s+(get|post|put|patch|delete)|axioserror|error:|network error|internal server error|failed with status code)/i.test(message.trim()) ||
    /<\/?[a-z][\s\S]*>/i.test(message) ||
    /\/api\/v\d/i.test(message);
}

function response<T>(result: any, successMessage: string, errorMessage: string): Response<T> {
  const success = result?.success !== false && result?.status >= 200 && result?.status < 300;
  const message = apiMessage(result);
  const hasData = result?.data && Object.prototype.hasOwnProperty.call(result.data, 'data');
  return {
    success,
    message: success ? successMessage : translate(message) || (isTechnicalMessage(message) ? errorMessage : message),
    data: success ? (hasData ? result.data.data : result?.data) : undefined
  };
}

export function RemunerationBalancesProvider({ children }: { children: ReactNode }) {
  async function getRemunerationBalances(filter: Filter = {}) {
    const params = Object.entries(filter).reduce<Record<string, unknown>>((current, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') current[key] = value;
      return current;
    }, {});
    const result = await apiV2.get('/remuneration-balances', { params });
    return response<RemunerationBalance[]>(result, 'Saldos de remuneração consultados com sucesso!', 'Não foi possível consultar os saldos de remuneração.');
  }

  async function getRemunerationBalance(balanceId: EntityId) {
    const result = await apiV2.get(`/remuneration-balances/${balanceId}`);
    return response<RemunerationBalance>(result, 'Saldo de remuneração consultado com sucesso!', 'Não foi possível consultar o saldo de remuneração.');
  }

  async function getCancellableRemunerationBalancePayments(filter: CancellablePaymentsFilter = {}) {
    const params = Object.entries(filter).reduce<Record<string, unknown>>((current, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') current[key] = value;
      return current;
    }, {});
    const result = await apiV2.get('/remuneration-balances/payments', { params });
    return response<CancellableRemunerationBalancePayment[]>(result, 'Pagamentos de saldo consultados com sucesso!', 'Não foi possível consultar os pedidos pendentes de diferença de produção.');
  }

  async function createRemunerationBalancePayment(allocations: Array<{ origin_id: EntityId; amount: number }>) {
    const result = await apiV2.post('/remuneration-balances/settlements/payment', { allocations });
    return response<undefined>(result, 'Pedido de diferença de produção gerado com sucesso!', 'Não foi possível gerar o pedido de diferença de produção.');
  }

  async function cancelRemunerationBalancePayment(settlementId: EntityId) {
    const result = await apiV2.delete(`/remuneration-balances/settlements/${settlementId}`);
    return response<undefined>(result, 'Pedido de diferença de produção removido com sucesso!', 'Não foi possível remover o pedido de diferença de produção.');
  }

  async function payRemunerationBalanceSettlement(settlementId: EntityId, data: { payment_date: string; payment_type_id: EntityId }) {
    const result = await apiV2.post(`/remuneration-balances/settlements/${settlementId}/pay`, data);
    return response<undefined>(result, 'Pedido de diferença de produção pago com sucesso!', 'Não foi possível pagar o pedido de diferença de produção.');
  }

  async function reverseRemunerationBalanceSettlementPayment(settlementId: EntityId) {
    const result = await apiV2.post(`/remuneration-balances/settlements/${settlementId}/reverse-payment`);
    return response<undefined>(result, 'Pagamento do pedido de diferença de produção desfeito com sucesso!', 'Não foi possível desfazer o pagamento do pedido de diferença de produção.');
  }

  return <RemunerationBalancesContext.Provider value={{ getRemunerationBalances, getRemunerationBalance, getCancellableRemunerationBalancePayments, createRemunerationBalancePayment, cancelRemunerationBalancePayment, payRemunerationBalanceSettlement, reverseRemunerationBalanceSettlementPayment }}>{children}</RemunerationBalancesContext.Provider>;
}

export function UseRemunerationBalancesProvider() {
  return useContext(RemunerationBalancesContext);
}
